import { useCallback, useContext, useEffect, useState } from "react";
import AuthContext from "@/contexts/AuthContext";

import { useFormHandler } from "@/core/hooks/useFormHandler";
import { PlayersService } from "@/core/http/services/PlayersService";
import { RankingService } from "@/core/http/services/RankingService";
import { MatchesService } from "@/core/http/services/MatchesService";

const normalizeMatch = (match) => ({
  id: match?.id,
  finished: match?.finished ?? false,
  teamA: (match?.teamA?.players || []).map((player) => ({
    name: player?.name || "Jogador",
    avatarUrl: player?.avatarUrl || null,
  })),
  teamB: (match?.teamB?.players || []).map((player) => ({
    name: player?.name || "Jogador",
    avatarUrl: player?.avatarUrl || null,
  })),
  result:
    match?.teamA?.score !== null &&
    match?.teamA?.score !== undefined &&
    match?.teamB?.score !== null &&
    match?.teamB?.score !== undefined
      ? {
          teamA: match.teamA.score,
          teamB: match.teamB.score,
        }
      : null,
});

const normalizeTopThree = (player) => ({
  id: player?.player?.id ?? player?.id,
  position: player?.position ?? null,
  name: player?.player?.name ?? player?.name ?? "Jogador",
  points: player?.points ?? 0,
  wins: player?.wins ?? 0,
  avatarUrl: player?.player?.avatarUrl ?? player?.avatarUrl ?? null,
});

const useHome = () => {
  const { handleChange, formData, setField, removeField } = useFormHandler();
  const { userLoggedData } = useContext(AuthContext);
  const playerId = userLoggedData?.playerId;

  const [loading, setLoading] = useState({
    topThree: false,
    matches: false,
    savingResults: false,
  });
  const [error, setError] = useState({ type: null, message: null });

  const [topThreeData, setTopThreeData] = useState([]);
  const [matchesData, setMatchesData] = useState([]);
  const [matches, setMatches] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [rankingId, setRankingId] = useState(null);
  const [roundEndsAt, setRoundEndsAt] = useState(null);
  const [isAvailable, setIsAvailable] = useState(null);
  const [isEditingResults, setIsEditingResults] = useState(false);
  const [resultDraft, setResultDraft] = useState({});

  const buildResultDraft = useCallback((nextMatches = []) => {
    const initialDraft = {};
    nextMatches.forEach((match) => {
      initialDraft[match.id] = {
        teamA: match.result?.teamA?.toString() || "",
        teamB: match.result?.teamB?.toString() || "",
      };
    });
    return initialDraft;
  }, []);

  const getDashboard = useCallback(async () => {
    if (!playerId) {
      setDashboardData(null);
      setTopThreeData([]);
      setMatchesData([]);
      return;
    }

    setLoading((prev) => ({ ...prev, topThree: true, matches: true }));
    setError({ type: null, message: null });

    try {
      const { status, data, message } =
        await PlayersService.getDashboard(playerId);

      if (data?.error || status !== 200) {
        setError({
          type: "dashboard",
          message: message || "Erro ao buscar os dados do dashboard.",
        });
        return;
      }

      setDashboardData(data || null);
      setRankingId(
        data?.player?.rankingId ??
          data?.currentRound?.rankingId ??
          data?.rankingId ??
          null,
      );
      setRoundEndsAt(data?.currentRound?.endsAt ?? null);
      setIsAvailable(data?.player?.isAvailable ?? null);
      setTopThreeData((data?.topThree || []).map(normalizeTopThree));
      setMatchesData((data?.currentRound?.matches || []).map(normalizeMatch));
    } catch (err) {
      setError({
        type: "dashboard",
        message:
          err?.message || "Erro inesperado ao buscar os dados do dashboard",
      });
    } finally {
      setLoading((prev) => ({ ...prev, topThree: false, matches: false }));
    }
  }, [playerId]);

  const initialize = useCallback(async () => {
    await Promise.all([getDashboard()]);
  }, [getDashboard]);

  useEffect(() => {
    const nextMatches = matchesData || [];
    setMatches(nextMatches);
    setResultDraft(buildResultDraft(nextMatches));
    setIsEditingResults(false);
  }, [buildResultDraft, matchesData]);

  const hasMatches = matches.length > 0;
  const isWithoutSeason =
    dashboardData &&
    dashboardData?.currentRound === null &&
    Array.isArray(dashboardData?.topThree) &&
    dashboardData.topThree.length === 0;
  const emptySeasonMessage = isWithoutSeason
    ? "Voce ainda não esta inscrito em nenhum ranking. Aguarde a abertura da competição ou fale com a administração."
    : null;
  const hasSubmittedResults =
    hasMatches && matches.every((match) => Boolean(match.result));
  const canSubmitAllResults =
    hasMatches &&
    matches.every((match) => {
      const draft = resultDraft[match.id];
      return Boolean(draft?.teamA?.trim() && draft?.teamB?.trim());
    });

  const handleEditResults = useCallback(() => {
    if (hasSubmittedResults) return;
    setIsEditingResults(true);
  }, [hasSubmittedResults]);

  const handleDraftChange = useCallback(
    (matchId, teamField) => (event) => {
      const value = event.target.value;
      if (value !== "" && !/^\d+$/.test(value)) return;

      setResultDraft((prev) => ({
        ...prev,
        [matchId]: {
          ...prev[matchId],
          [teamField]: value,
        },
      }));
    },
    [],
  );

  const setAvailability = useCallback(
    async (nextAvailable) => {
      if (!rankingId) return false;
      const { status } = await RankingService.updateMyAvailability(
        rankingId,
        nextAvailable,
      );
      const success = status === 200 || status === 204;
      if (success) setIsAvailable(nextAvailable);
      return success;
    },
    [rankingId],
  );

  const handleSaveResults = useCallback(async () => {
    if (!canSubmitAllResults) return;

    setLoading((prev) => ({ ...prev, savingResults: true }));
    setError({ type: null, message: null });

    try {
      const results = await Promise.all(
        matches.map((match) => {
          const draft = resultDraft[match.id];
          return MatchesService.updateResult(match.id, {
            team1Score: Number(draft.teamA),
            team2Score: Number(draft.teamB),
          });
        }),
      );

      const anyFailed = results.some(
        (r) => r.data?.error || (r.status !== 200 && r.status !== 204),
      );
      if (anyFailed) {
        setError({
          type: "saveResults",
          message: "Erro ao salvar um ou mais resultados. Tente novamente.",
        });
        return;
      }

      const updatedMatches = matches.map((match) => {
        const draft = resultDraft[match.id];
        return {
          ...match,
          result: {
            teamA: Number(draft.teamA),
            teamB: Number(draft.teamB),
          },
        };
      });

      setMatches(updatedMatches);
      setIsEditingResults(false);
    } catch (err) {
      setError({
        type: "saveResults",
        message: err?.message || "Erro inesperado ao salvar os resultados.",
      });
    } finally {
      setLoading((prev) => ({ ...prev, savingResults: false }));
    }
  }, [canSubmitAllResults, matches, resultDraft]);

  return {
    topThreeData,
    matchesData,
    matches,
    isWithoutSeason,
    emptySeasonMessage,
    resultDraft,
    isEditingResults,
    hasSubmittedResults,
    canSubmitAllResults,
    rankingId,
    roundEndsAt,
    isAvailable,
    setAvailability,
    error,
    loading,
    initialize,
    handleEditResults,
    handleDraftChange,
    handleSaveResults,
    setField,
    removeField,
    handleChange,
    formData,
  };
};

export default useHome;
